import React, { useMemo } from 'react';
import { CheckCircleIcon, AlertTriangleIcon, XCircleIcon } from 'lucide-react';
import type { Division, LoanOfficer, MetricType, PeriodInfo, ProgressStatus } from '../../types';
import { KanbanCard } from './KanbanCard';
import { getProgressPercentage, calculateProjection, getProgressStatus } from '../../utils/calculations';
interface KanbanBoardProps {
  divisions: Division[];
  period: PeriodInfo;
  metric: MetricType;
}
interface EnrichedLO {
  loanOfficer: LoanOfficer;
  teamName: string;
  divisionName: string;
  projectedPercentage: number;
  status: ProgressStatus;
}
interface ColumnConfig {
  key: ProgressStatus;
  title: string;
  headerBg: string;
  icon: React.ReactNode;
  badgeBg: string;
  badgeText: string;
  emptyBorder: string;
}
const columns: ColumnConfig[] = [{
  key: 'on-track',
  title: 'On Track',
  headerBg: 'bg-emerald-500 dark:bg-emerald-600',
  icon: <CheckCircleIcon className="w-5 h-5" data-id="element-42" />,
  badgeBg: 'bg-white/90 dark:bg-gray-800/90',
  badgeText: 'text-emerald-700 dark:text-emerald-300',
  emptyBorder: 'border-emerald-100 dark:border-emerald-800'
}, {
  key: 'at-risk',
  title: 'At Risk',
  headerBg: 'bg-amber-500 dark:bg-amber-600',
  icon: <AlertTriangleIcon className="w-5 h-5" data-id="element-43" />,
  badgeBg: 'bg-white/90 dark:bg-gray-800/90',
  badgeText: 'text-amber-700 dark:text-amber-300',
  emptyBorder: 'border-amber-100 dark:border-amber-800'
}, {
  key: 'behind',
  title: 'Behind',
  headerBg: 'bg-red-500 dark:bg-red-600',
  icon: <XCircleIcon className="w-5 h-5" data-id="element-44" />,
  badgeBg: 'bg-white/90 dark:bg-gray-800/90',
  badgeText: 'text-red-700 dark:text-red-300',
  emptyBorder: 'border-red-100 dark:border-red-800'
}];
export function KanbanBoard({
  divisions,
  period,
  metric
}: KanbanBoardProps) {
  const grouped = useMemo(() => {
    // Flatten all LOs with enrichment
    const allLOs: EnrichedLO[] = [];
    for (const division of divisions) {
      for (const team of division.teams) {
        for (const lo of team.loanOfficers) {
          const current = metric === 'softPull' ? lo.softPullCount : lo.submittedCount;
          const goal = metric === 'softPull' ? lo.softPullGoal : lo.submittedGoal;
          const projected = calculateProjection(current, period.daysElapsed, period.totalDays);
          const projectedPercentage = getProgressPercentage(projected, goal);
          const status = getProgressStatus(projectedPercentage);
          allLOs.push({
            loanOfficer: lo,
            teamName: team.name,
            divisionName: division.name,
            projectedPercentage,
            status
          });
        }
      }
    }
    // Sort into groups, each sorted by projected percentage ascending (worst first)
    const result: Record<ProgressStatus, EnrichedLO[]> = {
      'on-track': [],
      'at-risk': [],
      behind: []
    };
    for (const lo of allLOs) {
      result[lo.status].push(lo);
    }
    // Sort each group: ascending projected percentage (lowest first within group)
    for (const key of Object.keys(result) as ProgressStatus[]) {
      result[key].sort((a, b) => a.projectedPercentage - b.projectedPercentage);
    }
    return result;
  }, [divisions, period, metric]);
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" role="region" aria-label="Status board" data-id="element-45">
      {columns.map(col => {
      const items = grouped[col.key];
      return <div key={col.key} className="rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col transition-colors" data-id="element-46">
            {/* Column Header */}
            <div className={`${col.headerBg} text-white px-5 py-4 flex items-center justify-between`} data-id="element-47">
              <div className="flex items-center gap-2.5" data-id="element-48">
                {col.icon}
                <span className="text-sm font-semibold" data-id="element-49">{col.title}</span>
              </div>
              <span className={`${col.badgeBg} ${col.badgeText} rounded-full px-3 py-1 text-xs font-bold shadow-sm`} data-id="element-50">
                {items.length}
              </span>
            </div>

            {/* Card List */}
            <div className="p-4 space-y-3 flex-1 max-h-[calc(100vh-380px)] overflow-y-auto" data-id="element-51">
              {items.length === 0 ? <div className={`flex items-center justify-center py-12 border-2 border-dashed ${col.emptyBorder} rounded-lg`} data-id="element-52">
                  <p className="text-sm text-gray-400 dark:text-gray-500" data-id="element-53">
                    No loan officers
                  </p>
                </div> : items.map(enriched => <KanbanCard key={enriched.loanOfficer.id} loanOfficer={enriched.loanOfficer} teamName={enriched.teamName} divisionName={enriched.divisionName} period={period} metric={metric} data-id="element-54" />)}
            </div>
          </div>;
    })}
    </div>;
}